// BB.2 — Dispatcher da rota /membros/meu-negocio/secao/$section.
//
// Decide para onde mandar o usuário com base no `resolveBusinessPlanEditor`:
// - guided       → redireciona para /membros/meu-projeto/missao/$id
// - structured   → renderiza StructuredSectionEditor
// - dependency   → mostra card explicativo + CTA para a dependência
// - unavailable  → mensagem clara sem CTA quebrado

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect } from "react";
import { AlertTriangle, ArrowRight, Briefcase, Compass, Lock } from "lucide-react";
import { getSectionEditor } from "@/lib/business-plan/editor.functions";
import { getStructuredConfig } from "@/lib/business-plan/structured-fields";
import { SECTION_BY_KEY, type BlockKey } from "@/lib/business-plan/schema";
import { StructuredSectionEditor } from "@/components/business-plan/StructuredSectionEditor";

export const Route = createFileRoute("/_authenticated/membros/meu-negocio/secao/$section")({
  head: () => ({
    meta: [
      { title: "Editar seção — Meu Negócio" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SectionEditorRoute,
});

function SectionEditorRoute() {
  const { section } = Route.useParams();
  const sectionKey = section as BlockKey;
  const navigate = useNavigate();
  const fetch = useServerFn(getSectionEditor);

  const { data, isLoading } = useQuery({
    queryKey: ["business-plan-editor", sectionKey],
    queryFn: () => fetch({ data: { sectionKey } }),
  });

  const resolution = data?.resolution ?? null;

  // Guided → redireciona para a missão canônica.
  useEffect(() => {
    if (resolution?.mode === "guided" && resolution.missionId) {
      navigate({
        to: "/membros/meu-projeto/missao/$id",
        params: { id: resolution.missionId },
        search: {},
        replace: true,
      });
    }
  }, [resolution, navigate]);

  if (isLoading) {
    return <div className="mx-auto h-72 max-w-3xl animate-pulse rounded-2xl bg-black/5" />;
  }

  const businessTitle = (data as any)?.businessTitle as string | null | undefined;

  if (!data || !resolution) {
    return (
      <SectionShell businessTitle={businessTitle}>
        <div className="mx-auto max-w-xl rounded-2xl border border-black/5 bg-white p-8 text-center">
          <p className="font-display text-lg font-extrabold text-[var(--navy)]">
            Seção não encontrada
          </p>
          <p className="mt-2 text-sm text-[var(--gray-ink)]">
            Verifique se você tem um projeto ativo.
          </p>
          <Link
            to="/membros/meu-negocio"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white"
          >
            Voltar ao plano
          </Link>
        </div>
      </SectionShell>
    );
  }

  const sectionDef = SECTION_BY_KEY[sectionKey];

  if (resolution.mode === "guided") {
    return <div className="mx-auto h-40 max-w-3xl animate-pulse rounded-2xl bg-black/5" />;
  }

  if (resolution.mode === "dependency" && resolution.dependencySectionKey) {
    const dep = SECTION_BY_KEY[resolution.dependencySectionKey];
    return (
      <SectionShell businessTitle={businessTitle}>
        <div className="mx-auto max-w-xl rounded-2xl border border-[var(--gold)]/30 bg-white p-6 lg:p-8">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--gold)]/15 text-[var(--gold)]">
            <Lock size={20} />
          </div>
          <p className="mt-4 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            Dependência
          </p>
          <h1 className="mt-1 font-display text-xl font-extrabold text-[var(--navy)]">
            Antes de "{sectionDef?.title ?? sectionKey}", defina "{dep?.title ?? resolution.dependencySectionKey}"
          </h1>
          <p className="mt-2 text-sm text-[var(--gray-ink)]">{resolution.reason}</p>
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row">
            <Link
              to="/membros/meu-negocio"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-black/10 bg-white px-4 py-2.5 text-sm font-semibold text-[var(--navy)]"
            >
              Voltar ao plano
            </Link>
            <Link
              to="/membros/meu-negocio/secao/$section"
              params={{ section: resolution.dependencySectionKey }}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--navy)] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[var(--navy)]/20 hover:-translate-y-0.5 transition"
            >
              {resolution.cta} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </SectionShell>
    );
  }

  if (resolution.mode === "unavailable") {
    return (
      <SectionShell businessTitle={businessTitle}>
        <div className="mx-auto max-w-xl rounded-2xl border border-black/5 bg-white p-6 text-center lg:p-8">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-black/5 text-[var(--gray-ink)]">
            <AlertTriangle size={20} />
          </div>
          <h1 className="mt-4 font-display text-xl font-extrabold text-[var(--navy)]">
            Esta seção ainda não possui um editor disponível
          </h1>
          <p className="mt-2 text-sm text-[var(--gray-ink)]">
            Estamos preparando conteúdo específico para "{sectionDef?.title ?? sectionKey}".
            Enquanto isso, continue construindo pelas seções liberadas.
          </p>
          <Link
            to="/membros/meu-negocio"
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white"
          >
            <Compass size={14} /> Voltar ao plano
          </Link>
        </div>
      </SectionShell>
    );
  }

  // structured
  const config = getStructuredConfig(sectionKey);
  if (!config) {
    return (
      <SectionShell businessTitle={businessTitle}>
        <div className="mx-auto max-w-xl rounded-2xl border border-black/5 bg-white p-6 text-center">
          <p className="text-sm text-[var(--gray-ink)]">Configuração indisponível.</p>
        </div>
      </SectionShell>
    );
  }
  return (
    <SectionShell businessTitle={businessTitle}>
      <StructuredSectionEditor config={config} initial={data.workspaceBlock ?? null} />
    </SectionShell>
  );
}

function SectionShell({ businessTitle, children }: { businessTitle?: string | null; children: React.ReactNode }) {
  return (
    <div>
      {businessTitle && (
        <div className="mx-auto mb-4 flex max-w-3xl items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-[var(--navy)]/8 text-[var(--navy)]">
            <Briefcase size={12} />
          </span>
          <span className="text-[var(--gray-ink)]">Negócio em construção</span>
          <span className="text-black/20">·</span>
          <span className="truncate font-display text-[13px] font-extrabold normal-case tracking-normal text-[var(--navy)]">
            {businessTitle}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}
